import { useSyncExternalStore } from 'react';
import { onConsentChange } from '@fff-web/consent';

declare global {
  interface Window {
    OneTrust: unknown;
  }
}

const OPT_OUT_STATES = ['CA', 'CO', 'TX', 'VA', 'NE', 'MN'];

/** OneTrust populates its geolocation shortly after the stub script loads. */
const POLL_INTERVAL_MS = 500;
const POLL_TIMEOUT_MS = 5000;

const readGeoState = (): string => {
  try {
    return window?.OneTrust?.getGeolocationData()?.state || '';
  } catch {
    return '';
  }
};

const subscribeGeoState = (onStoreChange: () => void): (() => void) => {
  if (typeof window === 'undefined' || readGeoState()) return () => undefined;

  const resolve = () => {
    if (!readGeoState()) return;
    onStoreChange();
    cleanup();
  };

  const interval = setInterval(resolve, POLL_INTERVAL_MS);

  // Stop polling once OneTrust's expected load window elapses. A blocked, failed, or
  // stateless (non-US) geolocation would otherwise poll for the whole page lifetime; the
  // event listener below still recovers a late load without an endless timer.
  const timeout = setTimeout(() => clearInterval(interval), POLL_TIMEOUT_MS);

  const unsubscribe = onConsentChange(resolve);

  function cleanup() {
    clearInterval(interval);
    clearTimeout(timeout);
    unsubscribe();
  }

  return cleanup;
};

const useGeoState = (): string => useSyncExternalStore(subscribeGeoState, readGeoState, () => '');

const isOptOutState = (state: string): boolean => OPT_OUT_STATES.includes(state.toUpperCase());

export { isOptOutState, useGeoState };
